import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import lessonsProgressService from '../services/lessonsProgressService';

// Mock lesson data
const mockLessons = {
  physics: [
    {
      id: 'physics-2-5',
      title: '2.5 - Friction',
      topics: ['What friction is', 'The advantages of friction', 'The disadvantages of friction'],
      duration: 5,
      completed: false,
      progress: 0
    },
    {
      id: 'physics-2-6',
      title: '2.6 - Air & Water Resistance',
      topics: ['Types of resistance', 'Factors affecting resistance', 'Reducing resistance'],
      duration: 6,
      completed: false,
      progress: 0
    },
    {
      id: 'physics-2-7',
      title: '2.7 - Forces in Equilibrium',
      topics: ['Balanced forces', 'Force diagrams', 'Resultant forces'],
      duration: 7,
      completed: false,
      progress: 0
    }
  ],
  chemistry: [
    {
      id: 'chemistry-1-1',
      title: '1.1 - Atomic Structure',
      topics: ['Protons, neutrons, electrons', 'Atomic number', 'Mass number'],
      duration: 8,
      completed: false,
      progress: 0
    },
    {
      id: 'chemistry-1-2',
      title: '1.2 - Electronic Configuration',
      topics: ['Electron shells', 'Electronic structure', 'Noble gas configuration'],
      duration: 6,
      completed: false,
      progress: 0
    }
  ],
  biology: [
    {
      id: 'biology-1-1',
      title: '1.1 - Cell Structure',
      topics: ['Plant cells', 'Animal cells', 'Cell organelles'],
      duration: 10,
      completed: false,
      progress: 0
    },
    {
      id: 'biology-1-2',
      title: '1.2 - Cell Division',
      topics: ['Mitosis', 'Meiosis', 'Cell cycle'],
      duration: 12,
      completed: false,
      progress: 0
    }
  ]
};

export default function Lessons() {
  const [activeSubject, setActiveSubject] = useState('physics');
  const [lessonsProgress, setLessonsProgress] = useState({});
  const [overallStats, setOverallStats] = useState(null);

  // Load progress data and initialize mock data if needed
  useEffect(() => {
    const loadProgress = () => {
      const allProgress = lessonsProgressService.getAllProgress();
      setLessonsProgress(allProgress);

      // Initialize with mock data if no progress exists
      if (Object.keys(allProgress).length === 0) {
        lessonsProgressService.initializeMockProgress();
        setLessonsProgress(lessonsProgressService.getAllProgress());
      }

      // Calculate overall stats
      const stats = lessonsProgressService.getOverallStats();
      setOverallStats(stats);
    };

    loadProgress();
  }, []);

  // Get progress for a specific lesson
  const getLessonProgress = (lessonId) => {
    return lessonsProgress[lessonId] || { progress: 0, completed: false };
  };

  // Calculate total completed lessons across all subjects
  const getTotalCompleted = () => {
    return Object.values(mockLessons).flat().filter(lesson =>
      getLessonProgress(lesson.id).completed
    ).length;
  };

  // Calculate total lessons across all subjects
  const getTotalLessons = () => {
    return Object.values(mockLessons).flat().length;
  };

  // Calculate subject-specific stats
  const getSubjectStats = (subject) => {
    const subjectLessons = mockLessons[subject];
    const completed = subjectLessons.filter(lesson =>
      getLessonProgress(lesson.id).completed
    ).length;
    return { completed, total: subjectLessons.length };
  };

  const subjects = [
    { id: 'physics', name: 'Physics', icon: '⚛️', color: 'blue' },
    { id: 'chemistry', name: 'Chemistry', icon: '🧪', color: 'green' },
    { id: 'biology', name: 'Biology', icon: '🧬', color: 'purple' }
  ];

  const getSubjectStyle = (subject, isActive) => {
    const colors = {
      physics: isActive ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
      chemistry: isActive ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100',
      biology: isActive ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
    };
    return colors[subject];
  };

  const getProgressColor = (subject) => {
    const colors = {
      physics: 'bg-blue-500',
      chemistry: 'bg-green-500',
      biology: 'bg-purple-500'
    };
    return colors[subject];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">📖 IGCSE Lessons</h1>
              <p className="text-gray-600 mt-1">Comprehensive video-free lessons with interactive diagrams</p>
            </div>
            <div className="hidden sm:flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                <span className="font-medium">Progress:</span> {getTotalCompleted()}/{getTotalLessons()} lessons completed
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getTotalLessons() > 0 ? Math.round((getTotalCompleted() / getTotalLessons()) * 100) : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subject Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {subjects.map((subject) => (
              <button
                key={subject.id}
                onClick={() => setActiveSubject(subject.id)}
                className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md font-medium transition-colors ${
                  getSubjectStyle(subject.id, activeSubject === subject.id)
                }`}
              >
                <span className="mr-2">{subject.icon}</span>
                {subject.name}
              </button>
            ))}
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mockLessons[activeSubject].map((lesson) => {
            const progress = getLessonProgress(lesson.id);
            return (
              <Link
                key={lesson.id}
                to={`/lessons/${lesson.id}`}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
              >
                <div className="p-6">
                  {/* Lesson Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{lesson.title}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="mr-4">⏱️ {lesson.duration} mins</span>
                        <span>📑 {lesson.topics.length} topics</span>
                      </div>
                    </div>
                    {progress.completed && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✅ Complete
                        </span>
                      </div>
                    )}
                    {progress.progress > 0 && !progress.completed && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          📚 In Progress
                        </span>
                      </div>
                    )}
                  </div>

                {/* Topics Preview */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">What you'll learn:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {lesson.topics.map((topic, index) => (
                      <li key={index} className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></span>
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium">{progress.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(activeSubject)}`}
                        style={{ width: `${progress.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {progress.progress === 0 ? 'Start lesson' : progress.completed ? 'Review lesson' : 'Continue lesson'}
                      </span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Subject Stats */}
        <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {subjects.find(s => s.id === activeSubject)?.icon} {subjects.find(s => s.id === activeSubject)?.name} Progress
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{mockLessons[activeSubject].length}</div>
              <div className="text-sm text-gray-600">Total Lessons</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{getSubjectStats(activeSubject).completed}</div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {mockLessons[activeSubject].reduce((sum, lesson) => sum + lesson.duration, 0)} mins
              </div>
              <div className="text-sm text-gray-600">Total Duration</div>
            </div>
          </div>

          {/* Subject Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Subject Progress</span>
              <span className="font-medium">{getSubjectStats(activeSubject).averageProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(activeSubject)}`}
                style={{ width: `${getSubjectStats(activeSubject).averageProgress}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Coming Soon */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center">
            <span className="text-2xl mr-3">🚀</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">More subjects coming soon!</h3>
              <p className="text-gray-600">Mathematics, Computer Science, and Business Studies lessons are in development.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}